import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { EditorType } from '@components/Editor/EditorTypeExtension'

// 单个编辑器位置快照
export interface EditorPosition {
  // ProseMirror 选区位置（from/to），null 表示无选区记录
  selection: { from: number; to: number } | null
  // 编辑器滚动容器的 scrollTop
  scrollTop: number
  // 保存时间戳，便于调试与未来 LRU 清理
  savedAt: number
}

// 合并写入时使用的部分字段（避免 selection 与 scrollTop 互相覆盖）
export type PartialEditorPosition = Partial<Omit<EditorPosition, 'savedAt'>>

export interface EditorPositionsState {
  // 按 editorType 分组，每组下按 noteId 存储位置
  // 同一笔记在 main 与 right 编辑器的位置独立保存
  positions: Partial<Record<EditorType, Record<string, EditorPosition>>>
}

const initialState: EditorPositionsState = {
  positions: {}
}

interface SavePositionPayload {
  editorType: EditorType
  noteId: string
  position: PartialEditorPosition
}

interface ClearPositionPayload {
  editorType: EditorType
  noteId: string
}

const editorPositionsSlice = createSlice({
  name: 'editorPositions',
  initialState,
  reducers: {
    // 合并写入位置（selection 与 scrollTop 独立合并，避免互相覆盖）
    saveEditorPosition: (state, action: PayloadAction<SavePositionPayload>) => {
      const { editorType, noteId, position } = action.payload
      if (!editorType || !noteId) return
      if (!state.positions[editorType]) {
        state.positions[editorType] = {}
      }
      const bucket = state.positions[editorType]!
      const existing = bucket[noteId] || { selection: null, scrollTop: 0, savedAt: 0 }
      bucket[noteId] = {
        selection: position.selection !== undefined ? position.selection : existing.selection,
        scrollTop: position.scrollTop !== undefined ? position.scrollTop : existing.scrollTop,
        savedAt: Date.now()
      }
    },
    // 清理单条位置（关闭笔记时可选调用）
    clearEditorPosition: (state, action: PayloadAction<ClearPositionPayload>) => {
      const { editorType, noteId } = action.payload
      const bucket = state.positions[editorType]
      if (bucket) {
        delete bucket[noteId]
      }
    }
  }
})

export const { saveEditorPosition, clearEditorPosition } = editorPositionsSlice.actions
export default editorPositionsSlice.reducer
